import React, { FC } from 'react';
import { MutationResult, QueryResult } from 'react-apollo';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Dialog, DialogContent } from '@material-ui/core';
import { DataProxy } from 'apollo-cache';
import { DocumentNode } from 'graphql';

import { GetSharePreviewInfoComponent, SharesItem } from 'api';
import { QueryManager } from 'components';
import { useContent } from 'hooks';

import Form from './Form';
import { Title, Transition } from '../components';

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  refetchQueries?: (
    mutationResult: MutationResult,
  ) => Array<{ query: DocumentNode; variables?: object }>;
  onDelete?: (cache: DataProxy, result: MutationResult['data']) => void;
  onUpdate?: (cache: DataProxy, result: MutationResult['data']) => void;
  existingShares?: SharesItem[];
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      paddingTop: theme.spacing(4),
    },
    content: {
      position: 'relative',
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(5),
      backgroundColor: theme.palette.background.default,
    },
    divider: {
      height: 3,
      backgroundColor: theme.palette.primary.main,
    },
  });

const ShareDialog: FC<IProps> = props => {
  const { open, classes, existingShares, refetchQueries, onClose, onUpdate, onDelete } = props;

  const content = useContent();
  const variables = { content_id: content.content_id };

  function track() {
    window.analytics &&
      window.analytics.track('Share Initiated', {
        content_id: content.content_id,
      });
  }

  function renderContent(queryResult: QueryResult) {
    const { data } = queryResult;

    if (!data || !data.getSharePreviewInfo) {
      return null;
    }

    const { getSharePreviewInfo } = data;
    track();

    return (
      <Form
        previewInfo={getSharePreviewInfo}
        onClose={onClose}
        onConnect={queryResult.refetch}
        existingShares={existingShares}
        refetchQueries={refetchQueries}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    );
  }

  return (
    <Dialog
      fullScreen
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}>
      <Title />
      <DialogContent className={classes.content}>
        <GetSharePreviewInfoComponent fetchPolicy="network-only" variables={variables}>
          {queryProps => (
            <QueryManager {...queryProps}>{() => renderContent(queryProps)}</QueryManager>
          )}
        </GetSharePreviewInfoComponent>
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(styles)(ShareDialog);
